/* ============================================================
   MANSIÓN MONARA — CONCEPTO 4 — JAVASCRIPT
   Shopify Template | Claude Version
   ============================================================ */

'use strict';

window.MONARA = window.MONARA || {};

/* ── Helpers ─────────────────────────────────────────────── */
/* Para valores en centavos: /products/{handle}.js, cart.js, o el product|json de Liquid */
MONARA.formatMoney = (cents) => {
  const n = parseFloat(cents);
  if (isNaN(n) || cents == null) return '';
  return '$' + (n / 100).toFixed(0).replace(/\B(?=(\d{3})+(?!\d))/g, ',') + ' MXN';
};

/* Para valores ya en pesos: /collections/{handle}/products.json, /products.json */
MONARA.formatMoneyFromPesos = (pesos) => {
  const n = parseFloat(pesos);
  if (isNaN(n) || pesos == null) return '';
  return '$' + n.toFixed(0).replace(/\B(?=(\d{3})+(?!\d))/g, ',') + ' MXN';
};

/* ── Cart Drawer ─────────────────────────────────────────── */
MONARA.CartDrawer = {
  drawer:  null,
  overlay: null,

  init() {
    this.drawer  = document.querySelector('.mm-cart-drawer');
    this.overlay = document.querySelector('.mm-cart-overlay');
    if (!this.drawer) return;

    document.querySelectorAll('[data-mm-cart-open]').forEach(el =>
      el.addEventListener('click', () => this.open())
    );

    this.overlay?.addEventListener('click', () => this.close());
    this.drawer.querySelector('.mm-cart-drawer__close')
        ?.addEventListener('click', () => this.close());

    document.addEventListener('keydown', e => {
      if (e.key === 'Escape' && this.drawer.classList.contains('is-open')) this.close();
    });
  },

  open() {
    this.drawer.classList.add('is-open');
    this.overlay?.classList.add('is-open');
    document.body.style.overflow = 'hidden';
    this.fetchAndRender();
  },

  close() {
    this.drawer.classList.remove('is-open');
    this.overlay?.classList.remove('is-open');
    document.body.style.overflow = '';
  },

  async fetchAndRender() {
    try {
      const res  = await fetch('/cart.js');
      const cart = await res.json();
      this.render(cart);
    } catch (e) { console.error('Cart fetch:', e); }
  },

  render(cart) {
    const itemsEl   = this.drawer.querySelector('.mm-cart-drawer__items');
    const titleEl   = this.drawer.querySelector('.mm-cart-drawer__title');
    const totalEl   = this.drawer.querySelector('[data-mm-cart-total]');
    const countEls  = document.querySelectorAll('[data-mm-cart-count]');

    if (titleEl) titleEl.textContent = `Tu Carrito (${cart.item_count})`;
    if (totalEl) totalEl.textContent = MONARA.formatMoney(cart.total_price);
    countEls.forEach(el => {
      el.textContent = cart.item_count;
      el.style.display = cart.item_count > 0 ? 'flex' : 'none';
    });

    if (!itemsEl) return;

    if (cart.item_count === 0) {
      itemsEl.innerHTML = '<p style="text-align:center;padding:48px 0;color:var(--mm-on-surface-variant);font-style:italic;">Tu carrito está vacío.</p>';
      return;
    }

    itemsEl.innerHTML = cart.items.map(item => `
      <div class="mm-cart-item" data-line-key="${item.key}">
        <img class="mm-cart-item__img" src="${item.image}" alt="${item.product_title}">
        <div>
          <p class="mm-cart-item__brand">${item.vendor}</p>
          <p class="mm-cart-item__title">${item.product_title}</p>
          ${item.variant_title && item.variant_title !== 'Default Title'
            ? `<p class="mm-cart-item__variant">${item.variant_title}</p>` : ''}
          <p class="mm-cart-item__price">${MONARA.formatMoney(item.final_line_price)}</p>
          <div class="mm-cart-item__qty">
            <button class="mm-cart-item__qty-btn" data-qty-action="dec" data-key="${item.key}">−</button>
            <span class="mm-cart-item__qty-val">${item.quantity}</span>
            <button class="mm-cart-item__qty-btn" data-qty-action="inc" data-key="${item.key}">+</button>
          </div>
        </div>
        <button class="mm-cart-item__remove" data-key="${item.key}" aria-label="Eliminar">✕</button>
      </div>
    `).join('');

    itemsEl.querySelectorAll('[data-qty-action]').forEach(btn => {
      btn.addEventListener('click', () => {
        const key = btn.dataset.key;
        const row = btn.closest('.mm-cart-item');
        const val = parseInt(row?.querySelector('.mm-cart-item__qty-val')?.textContent || '1');
        this.changeQty(key, btn.dataset.qtyAction === 'inc' ? val + 1 : Math.max(0, val - 1));
      });
    });

    itemsEl.querySelectorAll('.mm-cart-item__remove').forEach(btn => {
      btn.addEventListener('click', () => this.changeQty(btn.dataset.key, 0));
    });
  },

  async changeQty(key, qty) {
    try {
      const res  = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: key, quantity: qty }),
      });
      const cart = await res.json();
      this.render(cart);
    } catch (e) { console.error('Cart change:', e); }
  },

  async addItem(variantId, qty = 1) {
    try {
      await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: variantId, quantity: qty }),
      });
      this.open();
    } catch (e) { console.error('Cart add:', e); }
  },
};

/* ── Comprar ahora — agrega al carrito y va directo a checkout ──
   Usa el mismo mecanismo nativo que ya probamos confiable para
   "Proceder al pago" (form POST a /cart con name="checkout"), en vez
   de /checkout?variantId=... que solo funcionaba si el carrito ya
   traía algo de antes — con carrito vacío, Shopify rebota al
   referrer en lugar de ir a checkout. */
MONARA.buyNow = async (variantId, quantity = 1) => {
  try {
    await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: variantId, quantity }),
    });
  } catch (e) { console.error('Buy now add:', e); }

  const form = document.createElement('form');
  form.action = '/cart';
  form.method = 'post';
  form.style.display = 'none';
  const input = document.createElement('input');
  input.type = 'hidden';
  input.name = 'checkout';
  input.value = 'Checkout';
  form.appendChild(input);
  document.body.appendChild(form);
  form.submit();
};

/* ── Quick-Add Modal ─────────────────────────────────────── */
MONARA.QuickAdd = {
  modal: null,

  init() {
    this.modal = document.querySelector('#mm-quickadd-modal');
    if (!this.modal) return;

    document.addEventListener('click', async e => {
      const trigger = e.target.closest('[data-mm-quickadd]');
      if (!trigger) return;
      e.preventDefault();
      await this.load(trigger.dataset.mmQuickadd, trigger.dataset.sizeGuideImg, trigger.dataset.sizeGuideTitle);
    });

    this.modal.addEventListener('click', e => {
      if (e.target === this.modal) this.close();
    });
    document.addEventListener('keydown', e => {
      if (e.key === 'Escape') this.close();
    });
  },

  async load(handle, sizeGuideImg, sizeGuideTitle) {
    try {
      const res     = await fetch(`/products/${handle}.js`);
      const product = await res.json();
      this.render(product, sizeGuideImg, sizeGuideTitle);
      this.open();
    } catch (e) { console.error('QuickAdd load:', e); }
  },

  render(product, sizeGuideImg, sizeGuideTitle) {
    let selectedVariant = product.variants.find(v => v.available) || product.variants[0];

    const sizesHtml = product.variants.map(v => `
      <button class="mm-pdp__size-btn ${v.id === selectedVariant?.id ? 'is-active' : ''} ${!v.available ? 'is-soldout' : ''}"
              data-variant-id="${v.id}" ${!v.available ? 'disabled' : ''}>
        ${v.title === 'Default Title' ? 'Talla única' : v.title}
      </button>
    `).join('');

    this.modal.innerHTML = `
      <div class="mm-modal">
        <div class="mm-modal__header">
          <img class="mm-modal__img" src="${product.featured_image}" alt="">
          <div>
            <p class="mm-modal__brand">${product.vendor}</p>
            <p class="mm-modal__title">${product.title}</p>
            <p class="mm-modal__price">${MONARA.formatMoney(product.price)}</p>
          </div>
          <button class="mm-modal__close" aria-label="Cerrar">✕</button>
        </div>
        <div class="mm-modal__body">
          <div class="mm-modal__size-header">
            <span>Selecciona talla</span>
            ${sizeGuideImg ? `<button type="button" class="mm-modal__size-guide" data-size-guide-trigger data-size-guide-img="${sizeGuideImg}" data-size-guide-title="${sizeGuideTitle || 'Guía de tallas'}">Guía →</button>` : ''}
          </div>
          <div class="mm-modal__sizes">${sizesHtml}</div>
          <div class="mm-modal__ctas">
            <button class="mm-btn mm-btn--cta mm-btn--full" id="mm-modal-add">AGREGAR AL CARRITO</button>
            <button class="mm-btn mm-btn--primary mm-btn--full" id="mm-modal-buy">COMPRAR AHORA</button>
          </div>
        </div>
      </div>
    `;

    this.modal.querySelectorAll('.mm-pdp__size-btn:not(.is-soldout)').forEach(btn => {
      btn.addEventListener('click', () => {
        this.modal.querySelectorAll('.mm-pdp__size-btn').forEach(b => b.classList.remove('is-active'));
        btn.classList.add('is-active');
        selectedVariant = product.variants.find(v => v.id == btn.dataset.variantId);
      });
    });

    this.modal.querySelector('#mm-modal-add')?.addEventListener('click', async () => {
      if (!selectedVariant) { alert('Selecciona una talla'); return; }
      await MONARA.CartDrawer.addItem(selectedVariant.id);
      this.close();
    });

    this.modal.querySelector('#mm-modal-buy')?.addEventListener('click', async () => {
      if (!selectedVariant) { alert('Selecciona una talla'); return; }
      await MONARA.buyNow(selectedVariant.id);
    });

    this.modal.querySelector('.mm-modal__close')?.addEventListener('click', () => this.close());
  },

  open() {
    this.modal.classList.add('is-open');
    document.body.style.overflow = 'hidden';
  },

  close() {
    this.modal.classList.remove('is-open');
    document.body.style.overflow = '';
  },
};

/* ── Completa el Look ────────────────────────────────────────
   Se abre con [data-mm-look-trigger] (ver mm-main-product.liquid).
   Todo pasa dentro del modal, sin navegar a otra página. El pool de
   piezas ya llegó pre-filtrado por stock desde Liquid (>= N variantes
   disponibles); aquí se vuelve a verificar contra /products/{handle}.js
   por si el inventario cambió entre el render de la página y el clic —
   ese endpoint siempre trae el estado más reciente de Shopify. ──── */
MONARA.CompleteLook = {
  modal: null,
  colorMap: {},

  init() {
    this.modal = document.querySelector('#mm-look-modal');

    const mapEl = document.querySelector('#mm-look-color-map');
    if (mapEl) {
      try { this.colorMap = JSON.parse(mapEl.textContent) || {}; } catch { this.colorMap = {}; }
    }

    if (!this.modal) return;

    document.addEventListener('click', async e => {
      const trigger = e.target.closest('[data-mm-look-trigger]');
      if (!trigger) return;
      e.preventDefault();
      await this.load(trigger);
    });

    this.modal.addEventListener('click', e => {
      if (e.target === this.modal) this.close();
    });
    document.addEventListener('keydown', e => {
      if (e.key === 'Escape') this.close();
    });
  },

  /* Se llama desde MONARA.PDPVariants cuando el cliente cambia de color
     swatch. Reemplaza al instante qué piezas ofrece "Completa el look"
     (sin recargar la página) para que combinen con el color elegido —
     usa el mapa color→look pre-calculado por Liquid en el servidor
     (#mm-look-color-map), que ya trae cada pieza pre-filtrada por stock.
     Si el color no tiene look propio, cae al "__default__". */
  syncColor(colorValue) {
    if (!colorValue) return;
    const key   = String(colorValue).toLowerCase().trim();
    const entry = this.colorMap[key] || this.colorMap.__default__;
    if (!entry) return;

    const trigger = document.querySelector('[data-mm-look-trigger]');
    const strip   = document.querySelector('[data-mm-look-strip]');
    if (!trigger || !strip) return;

    const section = strip.closest('.mm-section');
    if (section) section.hidden = entry.items.length === 0;
    if (entry.items.length === 0) return;

    trigger.dataset.mmLookId          = entry.lookId;
    trigger.dataset.mmLookAnchor      = entry.anchor;
    trigger.dataset.mmLookMinVariants = entry.minVariants;
    trigger.dataset.mmLookHandles     = entry.items.map(i => i.handle).join(',');

    const countEl = trigger.querySelector('[data-mm-look-count]');
    if (countEl) countEl.textContent = entry.items.length + 1;

    // La foto editorial grande de "Completa el look" no tiene su propio dato
    // de color por look — reaprovecha las fotos ya cargadas de la galería
    // principal del PDP (MONARA.PDPGallery.allImages), que sí vienen
    // etiquetadas por color vía el alt de cada imagen. Solo se toca si es
    // la foto por default (sin imagen editorial custom asignada en el editor).
    const mediaImg = document.querySelector('[data-mm-look-default-media]');
    if (mediaImg) {
      const colorImages = (MONARA.PDPGallery?.allImages || []).filter(img => img.alt === key);
      if (colorImages.length) mediaImg.src = colorImages[0].main;
    }

    strip.innerHTML = entry.items.slice(0, 3).map(item => `
      <div class="mm-look__row">
        <div class="mm-look__row-img"><img src="${item.image}" alt="${item.title}" loading="lazy"></div>
        <div class="mm-look__row-info">
          <p class="mm-look__row-vendor">${item.vendor}</p>
          <p class="mm-look__row-title">${item.title}</p>
          <p class="mm-look__row-price">${item.price}</p>
        </div>
        <span class="mm-look__row-stock">${item.stockLabel}</span>
      </div>
    `).join('');
  },

  async load(trigger) {
    const anchorHandle = trigger.dataset.mmLookAnchor;
    const lookId        = trigger.dataset.mmLookId;
    const minVariants    = parseInt(trigger.dataset.mmLookMinVariants || '3', 10);
    const handles = (trigger.dataset.mmLookHandles || '')
      .split(',').map(h => h.trim()).filter(Boolean);

    try {
      const [anchorProduct, ...complementProducts] = await Promise.all(
        [anchorHandle, ...handles].map(h => fetch(`/products/${h}.js`).then(r => r.json()))
      );

      const eligibleComplements = complementProducts.filter(
        p => p.variants.filter(v => v.available).length >= minVariants
      );
      if (eligibleComplements.length === 0) return;

      this.render(anchorProduct, eligibleComplements, lookId);
      this.open();
    } catch (e) { console.error('CompleteLook load:', e); }
  },

  itemRow(product) {
    const sizesHtml = product.variants.map(v => `
      <button class="mm-pdp__size-btn ${!v.available ? 'is-soldout' : ''}"
              data-variant-id="${v.id}" ${!v.available ? 'disabled' : ''}>
        ${v.title === 'Default Title' ? 'Talla única' : v.title}
      </button>
    `).join('');

    return `
      <div class="mm-look-modal__item" data-product-id="${product.id}">
        <div class="mm-look-modal__item-img"><img src="${product.featured_image}" alt="${product.title}"></div>
        <div class="mm-look-modal__item-info">
          <p class="mm-modal__brand">${product.vendor}</p>
          <p class="mm-modal__title" style="font-size:15px;">${product.title}</p>
          <p class="mm-modal__price">${MONARA.formatMoney(product.price)}</p>
          <div class="mm-look-modal__item-sizes">${sizesHtml}</div>
          <button type="button" class="mm-look-modal__item-add" disabled>AGREGAR</button>
        </div>
      </div>
    `;
  },

  render(anchorProduct, complements, lookId) {
    const items = [anchorProduct, ...complements];
    const cart  = {};

    this.modal.innerHTML = `
      <div class="mm-modal mm-modal--look">
        <button class="mm-modal__close" id="mm-look-close" aria-label="Cerrar"
                style="position:absolute;top:16px;right:16px;z-index:2;background:var(--mm-white);
                       width:34px;height:34px;border-radius:50%;display:flex;align-items:center;justify-content:center;">✕</button>
        <div class="mm-look-modal__images">
          ${items.map(p => `<img src="${p.featured_image}" alt="${p.title}">`).join('')}
        </div>
        <div class="mm-look-modal__panel">
          <div class="mm-look-modal__header">
            <p class="mm-look-modal__eyebrow">EL LOOK · ${items.length} PIEZAS DISPONIBLES</p>
            <h3 class="mm-look-modal__heading">Selecciona tu talla en cada pieza</h3>
          </div>
          <div class="mm-look-modal__list">
            ${items.map(p => this.itemRow(p)).join('')}
          </div>
          <div class="mm-look-modal__footer">
            <div>
              <p class="mm-look-modal__subtotal-label">SUBTOTAL DEL LOOK</p>
              <p class="mm-look-modal__subtotal-value" data-mm-look-subtotal>$0 MXN</p>
            </div>
            <button type="button" class="mm-btn mm-btn--cta" id="mm-look-add-all" disabled>Agregar el look completo</button>
          </div>
        </div>
      </div>
    `;

    const subtotalEl = this.modal.querySelector('[data-mm-look-subtotal]');
    const addAllBtn  = this.modal.querySelector('#mm-look-add-all');

    const updateSubtotal = () => {
      const total = items.filter(p => cart[p.id]).reduce((sum, p) => sum + p.price, 0);
      subtotalEl.textContent = MONARA.formatMoney(total);
      addAllBtn.disabled = items.every(p => cart[p.id]);
    };

    /* Cuando las N piezas del look ya están en el carrito, se etiqueta el
       cart con look_id vía cart attribute. La aplicación del descuento
       oculto (si se habilita) ocurre server-side leyendo ese atributo —
       el front nunca decide ni muestra ningún descuento al cliente. */
    const tagCartWithLook = async () => {
      if (!items.every(p => cart[p.id])) return;
      try {
        await fetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ attributes: { look_id: lookId } }),
        });
      } catch (e) { console.error('CompleteLook tag:', e); }
    };

    this.modal.querySelectorAll('.mm-look-modal__item').forEach(row => {
      const productId = row.dataset.productId;
      const addBtn    = row.querySelector('.mm-look-modal__item-add');
      let selectedVariantId = null;

      row.querySelectorAll('.mm-pdp__size-btn:not(.is-soldout)').forEach(btn => {
        btn.addEventListener('click', () => {
          row.querySelectorAll('.mm-pdp__size-btn').forEach(b => b.classList.remove('is-active'));
          btn.classList.add('is-active');
          selectedVariantId = btn.dataset.variantId;
          addBtn.disabled = false;
        });
      });

      addBtn.addEventListener('click', async () => {
        if (!selectedVariantId) return;
        try {
          await fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ id: selectedVariantId, quantity: 1 }),
          });
          cart[productId] = selectedVariantId;
          addBtn.textContent = 'AGREGADO ✓';
          addBtn.classList.add('is-added');
          addBtn.disabled = true;
          row.querySelectorAll('.mm-pdp__size-btn').forEach(b => b.disabled = true);
          updateSubtotal();
          await tagCartWithLook();
          MONARA.CartDrawer.fetchAndRender();
        } catch (e) { console.error('CompleteLook add item:', e); }
      });
    });

    addAllBtn.addEventListener('click', async () => {
      const toAdd = items.filter(p => !cart[p.id]).map(p => {
        const firstAvailable = p.variants.find(v => v.available);
        return firstAvailable ? { id: firstAvailable.id, quantity: 1 } : null;
      }).filter(Boolean);

      if (toAdd.length === 0) return;
      try {
        await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ items: toAdd }),
        });
        toAdd.forEach(entry => {
          const product = items.find(p => p.variants.some(v => v.id === entry.id));
          if (product) cart[product.id] = entry.id;
        });
        updateSubtotal();
        await tagCartWithLook();
        this.close();
        MONARA.CartDrawer.open();
      } catch (e) { console.error('CompleteLook add all:', e); }
    });

    this.modal.querySelector('#mm-look-close')?.addEventListener('click', () => this.close());
  },

  open() {
    this.modal.classList.add('is-open');
    document.body.style.overflow = 'hidden';
  },

  close() {
    this.modal.classList.remove('is-open');
    document.body.style.overflow = '';
  },
};

/* ── Guía de tallas — modal global (PDP, quick-add, PLP) ────
   Se abre con cualquier elemento [data-size-guide-trigger] que
   tenga data-size-guide-img (y opcionalmente data-size-guide-title).
   Nunca navega a otra página. ─────────────────────────────── */
MONARA.SizeGuide = {
  modal: null,

  init() {
    this.modal = document.querySelector('#mm-size-guide-modal');
    if (!this.modal) return;
    this.img   = document.querySelector('#mm-size-guide-img');
    this.title = document.querySelector('#mm-size-guide-title');

    document.addEventListener('click', e => {
      const trigger = e.target.closest('[data-size-guide-trigger]');
      if (!trigger) return;
      e.preventDefault();
      const src = trigger.dataset.sizeGuideImg;
      if (!src) return;
      this.open(src, trigger.dataset.sizeGuideTitle);
    });

    this.modal.addEventListener('click', e => {
      if (e.target === this.modal) this.close();
    });
    document.getElementById('mm-size-guide-close')?.addEventListener('click', () => this.close());
    document.addEventListener('keydown', e => {
      if (e.key === 'Escape') this.close();
    });
  },

  open(src, title) {
    if (!this.modal) return;
    if (this.img) this.img.src = src;
    if (this.title) this.title.textContent = title || 'Guía de tallas';
    this.modal.classList.add('is-open');
    document.body.style.overflow = 'hidden';
  },

  close() {
    if (!this.modal) return;
    this.modal.classList.remove('is-open');
    document.body.style.overflow = '';
  },
};

/* ── Color Swatch Swap (0ms instantáneo) ─────────────────── */
MONARA.SwatchSwap = {
  init() {
    document.addEventListener('mouseenter', e => {
      if (!(e.target instanceof Element)) return;
      const swatch = e.target.closest('[data-mm-swatch-img]');
      if (!swatch) return;
      const img = swatch.closest('.mm-card')?.querySelector('.mm-card__img');
      if (img) {
        img._originalSrc = img._originalSrc || img.src;
        img.src = swatch.dataset.mmSwatchImg;
      }
    }, true);

    document.addEventListener('mouseleave', e => {
      if (!(e.target instanceof Element)) return;
      const swatch = e.target.closest('[data-mm-swatch-img]');
      if (!swatch) return;
      const img = swatch.closest('.mm-card')?.querySelector('.mm-card__img');
      if (img?._originalSrc) img.src = img._originalSrc;
    }, true);
  },
};

/* ── PDP Gallery — track swipeable (scroll-snap) + lightbox ──
   Las fotos se arman en JS a partir de #mm-pdp-images-data (todas las
   imágenes del producto con su color en minúsculas vía texto
   alternativo). Al elegir un color se vuelve a renderizar todo
   (miniaturas, foto principal, puntos) solo con las fotos de ese
   color — si ninguna foto trae ese color en el alt, se muestran todas
   para no dejar la galería vacía. */
MONARA.PDPGallery = {
  current: 0,
  allImages: [],
  images: [],

  init() {
    const wrap   = document.getElementById('mm-pdp-img-wrap');
    const dataEl = document.getElementById('mm-pdp-images-data');
    if (!wrap || !dataEl) return;

    try { this.allImages = JSON.parse(dataEl.textContent) || []; } catch { this.allImages = []; }
    if (!this.allImages.length) return;

    this.wrap    = wrap;
    this.thumbsEl = document.getElementById('mm-pdp-thumbs');
    this.dotsEl   = document.getElementById('mm-pdp-dots');

    document.querySelector('.mm-pdp__arrow--prev')
      ?.addEventListener('click', () => this.goTo(this.current - 1));
    document.querySelector('.mm-pdp__arrow--next')
      ?.addEventListener('click', () => this.goTo(this.current + 1));

    let scrollTimeout;
    wrap.addEventListener('scroll', () => {
      clearTimeout(scrollTimeout);
      scrollTimeout = setTimeout(() => {
        const idx = Math.round(wrap.scrollLeft / (wrap.clientWidth || 1));
        this.syncActive(idx);
      }, 80);
    }, { passive: true });

    // El botón de lupa (no la imagen completa) abre el lightbox a pantalla
    // completa. Antes estaba en toda la imagen, pero en móvil un toque para
    // deslizar entre fotos también dispara "click" y abría el zoom por error,
    // tapando tallas/descripción/todo lo demás del PDP.
    document.getElementById('mm-pdp-zoom-btn')
      ?.addEventListener('click', () => MONARA.PDPLightbox.open(this.current, this.images));

    MONARA.PDPLightbox.init();

    const initialColor = document.querySelector('.mm-pdp__gallery')?.dataset.initialColor || '';
    this.filterByColor(initialColor);
  },

  /* Filtra la galería a solo las fotos cuyo alt coincida con este
     color; si ninguna coincide, muestra todas (evita galería vacía en
     productos que aún no tienen las fotos etiquetadas por color). */
  filterByColor(colorName) {
    const dc = (colorName || '').toLowerCase().trim();
    let filtered = dc ? this.allImages.filter(img => img.alt === dc) : this.allImages;
    if (!filtered.length) filtered = this.allImages;
    this.images = filtered;
    this.render();
  },

  render() {
    if (!this.wrap) return;

    this.wrap.innerHTML = this.images.map((img, i) => `
      <img class="mm-pdp__slide-img" data-index="${i}" src="${img.main}" alt=""
           loading="${i === 0 ? 'eager' : 'lazy'}">
    `).join('');
    this.slides = this.wrap.querySelectorAll('.mm-pdp__slide-img');

    if (this.thumbsEl) {
      this.thumbsEl.innerHTML = this.images.map((img, i) => `
        <div class="mm-pdp__thumb${i === 0 ? ' is-active' : ''}">
          <img src="${img.thumb}" alt="" width="200" height="200" loading="lazy">
        </div>
      `).join('');
      this.thumbs = this.thumbsEl.querySelectorAll('.mm-pdp__thumb');
      this.thumbs.forEach((thumb, i) => thumb.addEventListener('click', () => this.goTo(i)));
    }

    if (this.dotsEl) {
      this.dotsEl.innerHTML = this.images.map((_, i) => `
        <span class="mm-pdp__dot${i === 0 ? ' is-active' : ''}" data-index="${i}"></span>
      `).join('');
      this.dots = this.dotsEl.querySelectorAll('.mm-pdp__dot');
      this.dots.forEach((dot, i) => dot.addEventListener('click', () => this.goTo(i)));
    }

    this.current = 0;
    this.wrap.scrollLeft = 0;
  },

  goTo(index) {
    if (!this.slides?.length) return;
    const i = Math.max(0, Math.min(index, this.slides.length - 1));
    const slide = this.slides[i];
    if (slide && this.wrap) {
      this.wrap.scrollTo({ left: slide.offsetLeft, behavior: 'smooth' });
    }
    this.syncActive(i);
  },

  syncActive(index) {
    this.current = index;
    this.thumbs?.forEach((t, i) => t.classList.toggle('is-active', i === index));
    this.dots?.forEach((d, i) => d.classList.toggle('is-active', i === index));
  },
};

/* ── PDP Lightbox — galería a pantalla completa ─────────────── */
MONARA.PDPLightbox = {
  ready: false,

  init() {
    if (this.ready) return;
    this.el     = document.getElementById('mm-pdp-lightbox');
    this.track  = document.getElementById('mm-pdp-lightbox-track');
    this.dotsEl = document.getElementById('mm-pdp-lightbox-dots');
    if (!this.el || !this.track) return;
    this.ready = true;

    document.getElementById('mm-pdp-lightbox-close')
      ?.addEventListener('click', () => this.close());
    this.el.addEventListener('click', e => {
      if (e.target === this.el) this.close();
    });
    document.addEventListener('keydown', e => {
      if (e.key === 'Escape' && !this.el.hidden) this.close();
    });

    let scrollTimeout;
    this.track.addEventListener('scroll', () => {
      clearTimeout(scrollTimeout);
      scrollTimeout = setTimeout(() => {
        const idx = Math.round(this.track.scrollLeft / (this.track.clientWidth || 1));
        this.dots?.forEach((d, i) => d.classList.toggle('is-active', i === idx));
      }, 80);
    }, { passive: true });
  },

  /* images = arreglo de la galería visible actual (MONARA.PDPGallery.images),
     para que el visor a pantalla completa muestre solo las fotos del
     color que se está viendo, no todas las del producto. */
  open(index = 0, images = []) {
    if (!this.el || !this.track) return;

    this.track.innerHTML = images.map(img => `<img src="${img.zoom}" alt="" loading="lazy">`).join('');
    if (this.dotsEl) {
      this.dotsEl.innerHTML = images.map((_, i) => `
        <span class="mm-pdp__dot${i === index ? ' is-active' : ''}"></span>
      `).join('');
      this.dots = this.dotsEl.querySelectorAll('.mm-pdp__dot');
    }

    this.el.hidden = false;
    document.body.style.overflow = 'hidden';
    requestAnimationFrame(() => {
      const slide = this.track.children[index];
      if (slide) this.track.scrollLeft = slide.offsetLeft;
    });
  },

  close() {
    if (!this.el) return;
    this.el.hidden = true;
    document.body.style.overflow = '';
  },
};

/* ── PDP Variant Selection ───────────────────────────────── */
MONARA.PDPVariants = {
  init() {
    const form    = document.querySelector('[data-mm-pdp-form]');
    const jsonEl  = document.querySelector('[data-mm-product-json]');
    if (!form || !jsonEl) return;

    let product;
    try { product = JSON.parse(jsonEl.textContent); } catch { return; }

    const variantInput = form.querySelector('[name="id"]');
    const sizeBtns     = form.querySelectorAll('.mm-pdp__size-btn');
    const colorSwatches = form.querySelectorAll('.mm-pdp__color-swatch');
    const priceEl      = form.querySelector('[data-mm-price]');
    const msiEl        = form.querySelector('[data-mm-msi]');
    const addBtn       = form.querySelector('[data-mm-add-btn]');
    const buyNowBtn     = document.querySelector('[data-mm-buy-now]');

    // Detecta en qué posición (1, 2 o 3) vive cada opción por su
    // significado, no por que el nombre coincida exacto con
    // "Color"/"Talla" — Shopify nombra la opción distinto según la
    // categoría del producto (ej. Calzado usa "Talla del calzado"),
    // así que basta con que el nombre CONTENGA la palabra clave.
    const colorPos = product.options.findIndex(o => /color|colour/i.test(o)) + 1;
    const sizePos  = product.options.findIndex(o => /talla|size/i.test(o)) + 1;

    // Arranca con los valores de la variante ya seleccionada en el
    // servidor, para que si el cliente solo toca una opción (ej. solo
    // color) la otra (talla) no se pierda.
    const initialVariant = product.variants.find(v => String(v.id) === String(variantInput.value));
    const selectedOptionValues = {};
    if (initialVariant) {
      product.options.forEach((_, i) => {
        selectedOptionValues[i + 1] = initialVariant[`option${i + 1}`];
      });
    }

    const updateVariant = () => {
      const variant = product.variants.find(v =>
        Object.keys(selectedOptionValues).every(pos =>
          v[`option${pos}`] === selectedOptionValues[pos]
        )
      );
      if (!variant) return;
      variantInput.value = variant.id;

      const onSale = variant.compare_at_price > variant.price;
      if (priceEl) {
        priceEl.innerHTML = onSale
          ? `<span style="font-size:15px;font-weight:400;color:var(--mm-outline);text-decoration:line-through;margin-right:8px;">${MONARA.formatMoney(variant.compare_at_price)}</span>`
            + `<span style="color:var(--mm-error);">${MONARA.formatMoney(variant.price)}</span>`
          : MONARA.formatMoney(variant.price);
      }
      if (addBtn) addBtn.disabled = !variant.available;

      // Update MSI table. No aplica en variantes con rebaja/promoción; los
      // renglones solo existen en el DOM si Liquid los renderizó según el
      // umbral configurado, así que querySelectorAll no hace nada si no hay.
      if (msiEl) {
        msiEl.hidden = onSale;
        if (!onSale && variant.price) {
          msiEl.querySelectorAll('[data-msi-3]').forEach(el => el.textContent = MONARA.formatMoney(variant.price / 3));
          msiEl.querySelectorAll('[data-msi-6]').forEach(el => el.textContent = MONARA.formatMoney(variant.price / 6));
        }
      }
    };

    // Recalcula, con los datos reales de product.variants, qué tallas
    // existen y tienen stock para el color actualmente seleccionado.
    // Nada de listas fijas: si Shopify no tiene esa combinación
    // color+talla, o la tiene sin inventario, se marca agotada.
    const refreshSizeAvailability = () => {
      if (sizePos === 0) return;
      sizeBtns.forEach(btn => {
        const size = btn.dataset.size || btn.textContent.trim();
        const candidate = { ...selectedOptionValues, [sizePos]: size };
        const variant = product.variants.find(v =>
          Object.keys(candidate).every(pos => v[`option${pos}`] === candidate[pos])
        );
        const available = !!(variant && variant.available);
        btn.classList.toggle('is-soldout', !available);
        btn.disabled = !available;
        btn.setAttribute('aria-label', available ? '' : `${size} — Agotado`);
      });
    };
    refreshSizeAvailability();

    sizeBtns.forEach(btn => {
      btn.addEventListener('click', () => {
        if (btn.classList.contains('is-soldout')) return;
        sizeBtns.forEach(b => b.classList.remove('is-active'));
        btn.classList.add('is-active');
        if (sizePos > 0) {
          selectedOptionValues[sizePos] = btn.dataset.size || btn.textContent.trim();
        }
        updateVariant();
      });
    });

    const colorLabel = document.getElementById('mm-pdp-color-label');

    colorSwatches.forEach(swatch => {
      swatch.addEventListener('click', () => {
        colorSwatches.forEach(s => s.classList.remove('is-active'));
        swatch.classList.add('is-active');
        if (colorPos > 0) {
          // Usa data-value (respeta mayúsculas/minúsculas tal cual las
          // guarda Shopify, ej. "Cafe", "Negro") en vez de data-color
          // (siempre en minúsculas, solo para CSS/estilos del swatch).
          // Comparar contra option1/option2 con el valor en minúsculas
          // nunca encontraba variante y marcaba TODAS las tallas como
          // agotadas al cambiar de color.
          selectedOptionValues[colorPos] = swatch.dataset.value;
        }
        if (colorLabel && swatch.dataset.color) {
          colorLabel.textContent = swatch.dataset.color.toUpperCase();
        }
        refreshSizeAvailability();
        updateVariant();
        MONARA.PDPGallery.filterByColor?.(swatch.dataset.color);
        MONARA.CompleteLook.syncColor?.(swatch.dataset.color);
      });
    });

    form.addEventListener('submit', async e => {
      e.preventDefault();
      if (!variantInput.value) { alert('Selecciona una talla'); return; }
      addBtn.disabled = true;
      addBtn.textContent = 'Agregando...';
      await MONARA.CartDrawer.addItem(variantInput.value);
      addBtn.disabled = false;
      addBtn.textContent = addBtn.dataset.label || 'AGREGAR AL CARRITO';
    });

    buyNowBtn?.addEventListener('click', async () => {
      if (!variantInput.value) { alert('Selecciona una talla'); return; }
      buyNowBtn.disabled = true;
      buyNowBtn.textContent = 'Procesando...';
      await MONARA.buyNow(variantInput.value);
    });
  },
};

/* ── Infinite Scroll PLP ─────────────────────────────────── */
MONARA.InfiniteScroll = {
  init() {
    const grid     = document.querySelector('[data-mm-plp-grid]');
    const sentinel = document.querySelector('[data-mm-sentinel]');
    if (!grid || !sentinel) return;

    let page = 1, busy = false, done = false;

    const obs = new IntersectionObserver(entries => {
      if (!entries[0].isIntersecting || busy || done) return;
      busy = true;
      this.load(grid, ++page)
        .then(hasMore => { if (!hasMore) done = true; })
        .finally(() => { busy = false; });
    }, { rootMargin: '200px' });

    obs.observe(sentinel);
  },

  async load(grid, page) {
    const url = new URL(window.location.href);
    url.searchParams.set('page', page);

    const sentinel = document.querySelector('[data-mm-sentinel]');
    if (sentinel) {
      sentinel.innerHTML = this.skeletonHtml(4);
    }

    try {
      const res  = await fetch(url.href, { headers: { 'X-Requested-With': 'XMLHttpRequest' } });
      const html = await res.text();
      const doc  = new DOMParser().parseFromString(html, 'text/html');
      const cards = doc.querySelectorAll('[data-mm-plp-grid] .mm-card');

      if (sentinel) sentinel.innerHTML = '';

      if (!cards.length) return false;

      cards.forEach(card => {
        card.style.opacity = '0';
        card.style.transform = 'translateY(16px)';
        grid.insertBefore(card, sentinel);
        requestAnimationFrame(() => {
          card.style.transition = 'opacity 0.4s ease, transform 0.4s ease';
          card.style.opacity = '1';
          card.style.transform = '';
        });
      });

      return true;
    } catch { if (sentinel) sentinel.innerHTML = ''; return false; }
  },

  skeletonHtml(n) {
    return Array.from({ length: n }, () => `
      <div class="mm-card">
        <div class="mm-skeleton" style="aspect-ratio:3/4;margin-bottom:24px;"></div>
        <div class="mm-skeleton" style="height:10px;width:50%;margin-bottom:8px;"></div>
        <div class="mm-skeleton" style="height:14px;width:75%;margin-bottom:8px;"></div>
        <div class="mm-skeleton" style="height:15px;width:35%;"></div>
      </div>
    `).join('');
  },
};

/* ── Clips Feed ──────────────────────────────────────────── */
MONARA.ClipsFeed = {
  init() {
    const feed = document.querySelector('.mm-clips-feed');
    if (!feed) return;

    const clips = feed.querySelectorAll('.mm-clip');
    clips[0]?.querySelector('video')?.play().catch(() => {});

    const obs = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        const vid = entry.target.querySelector('video');
        if (!vid) return;
        entry.isIntersecting ? vid.play().catch(() => {}) : vid.pause();
      });
    }, { threshold: 0.7 });

    clips.forEach(clip => obs.observe(clip));

    feed.addEventListener('click', e => {
      const btn = e.target.closest('[data-mm-mute]');
      if (!btn) return;
      const vid = btn.closest('.mm-clip')?.querySelector('video');
      if (!vid) return;
      vid.muted = !vid.muted;
      btn.textContent = vid.muted ? '🔇' : '🔊';
    });
  },
};

/* ── Carousel Arrow Nav ──────────────────────────────────── */
MONARA.Carousels = {
  init() {
    document.querySelectorAll('.mm-carousel').forEach(carousel => {
      const track = carousel.querySelector('.mm-carousel__track');
      const prev  = carousel.querySelector('.mm-carousel__btn--prev');
      const next  = carousel.querySelector('.mm-carousel__btn--next');
      if (!track) return;

      const scrollBy = () => (track.querySelector('.mm-carousel__slide')?.offsetWidth || 280) + 24;

      prev?.addEventListener('click', () => track.scrollBy({ left: -scrollBy(), behavior: 'smooth' }));
      next?.addEventListener('click', () => track.scrollBy({ left:  scrollBy(), behavior: 'smooth' }));
    });
  },
};

/* ── Mobile Menu ─────────────────────────────────────────── */
MONARA.MobileMenu = {
  init() {
    const toggle  = document.querySelector('[data-mm-menu-toggle]');
    const drawer  = document.querySelector('[data-mm-menu-drawer]');
    const close   = document.querySelector('[data-mm-menu-close]');
    if (!toggle || !drawer) return;

    toggle.addEventListener('click', () => {
      drawer.classList.toggle('is-open');
      document.body.style.overflow = drawer.classList.contains('is-open') ? 'hidden' : '';
    });
    close?.addEventListener('click', () => {
      drawer.classList.remove('is-open');
      document.body.style.overflow = '';
    });
  },
};

/* ── Wishlist (localStorage) ─────────────────────────────── */
MONARA.Wishlist = {
  key: 'monara_wishlist',
  get()       { try { return JSON.parse(localStorage.getItem(this.key)) || []; } catch { return []; } },
  set(list)   { localStorage.setItem(this.key, JSON.stringify(list)); },
  has(id)     { return this.get().includes(String(id)); },
  toggle(id)  {
    const list = this.get(), sid = String(id), idx = list.indexOf(sid);
    if (idx > -1) list.splice(idx, 1); else list.push(sid);
    this.set(list);
    return idx === -1;
  },

  init() {
    this.get().forEach(id => {
      document.querySelectorAll(`[data-mm-wishlist="${id}"]`).forEach(btn =>
        btn.classList.add('is-active')
      );
    });

    document.addEventListener('click', e => {
      const btn = e.target.closest('[data-mm-wishlist]');
      if (!btn) return;
      e.preventDefault();
      const added = this.toggle(btn.dataset.mmWishlist);
      btn.classList.toggle('is-active', added);
      btn.style.transform = 'scale(1.3)';
      setTimeout(() => { btn.style.transform = ''; }, 180);
    });
  },
};

/* ── Filter Chips (PLP) ──────────────────────────────────── */
MONARA.FilterChips = {
  init() {
    document.querySelectorAll('.mm-filter-chip').forEach(chip => {
      chip.addEventListener('click', () => {
        const group = chip.closest('.mm-filter-chips');
        group?.querySelectorAll('.mm-filter-chip').forEach(c => c.classList.remove('is-active'));
        chip.classList.add('is-active');
      });
    });
  },
};

/* ── Newsletter ──────────────────────────────────────────── */
MONARA.Newsletter = {
  init() {
    document.querySelectorAll('[data-mm-newsletter]').forEach(form => {
      form.addEventListener('submit', async e => {
        e.preventDefault();
        const input = form.querySelector('[type="email"]');
        const btn   = form.querySelector('button[type="submit"]');
        const msg   = form.querySelector('[data-mm-nl-msg]');
        if (!input?.value) return;
        btn.disabled = true;
        btn.textContent = '...';
        try {
          const fd = new FormData();
          fd.append('form_type', 'customer');
          fd.append('email', input.value);
          await fetch('/contact', { method: 'POST', body: fd });
          if (msg) { msg.textContent = '¡Bienvenida a la mansión!'; msg.style.display = 'block'; }
          form.reset();
        } finally { btn.disabled = false; btn.textContent = 'SUSCRIBIRSE'; }
      });
    });
  },
};

/* ── Toast ───────────────────────────────────────────────── */
MONARA.showToast = (msg) => {
  const toast = document.getElementById('mm-toast');
  const msgEl  = document.getElementById('mm-toast-msg');
  if (!toast) return;
  if (msgEl) msgEl.textContent = msg;
  toast.classList.add('is-visible');
  clearTimeout(toast._hideTimer);
  toast._hideTimer = setTimeout(() => toast.classList.remove('is-visible'), 3200);
};

MONARA.initToast = () => {
  document.getElementById('mm-toast-close')?.addEventListener('click', () =>
    document.getElementById('mm-toast')?.classList.remove('is-visible')
  );
};

/* ── Home Video Grid — autoplay on scroll ────────────────── */
MONARA.HomeVideoGrid = {
  observer: null,

  init() {
    this.observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        const vid = entry.target;
        entry.isIntersecting ? vid.play().catch(() => {}) : vid.pause();
      });
    }, { threshold: 0.5 });
  },

  bindVideos(container) {
    container.querySelectorAll('video[data-mm-home-vid]').forEach(vid =>
      this.observer?.observe(vid)
    );
  },
};

/* ── Inline Size Picker ──────────────────────────────────── */
MONARA.InlineSizePicker = {

  init() {
    document.addEventListener('click', async e => {
      const btn = e.target.closest('[data-mm-inline-size]');
      if (!btn) return;
      e.preventDefault();
      e.stopPropagation();

      const handle = btn.dataset.mmInlineSize;
      const card   = btn.closest('.mm-card');
      if (!handle || !card) return;

      const tray = card.querySelector('.mm-card__size-tray');
      if (!tray) return;

      if (!tray.hidden) { this.closeTray(tray); return; }

      // Close any other open tray first
      document.querySelectorAll('.mm-card__size-tray:not([hidden])').forEach(t =>
        this.closeTray(t)
      );

      await this.openTray(tray, handle, card);
    });

    // Close when clicking outside a card
    document.addEventListener('click', e => {
      if (!e.target.closest('.mm-card')) {
        document.querySelectorAll('.mm-card__size-tray:not([hidden])').forEach(t =>
          this.closeTray(t)
        );
      }
    });
  },

  // No extra binding needed — fully delegated
  bindCards() {},

  async openTray(tray, handle, card) {
    tray.hidden = false;
    // Allow display before animating
    requestAnimationFrame(() => tray.classList.add('is-open'));

    const sizesEl = tray.querySelector('.mm-card__size-tray__sizes');
    if (!sizesEl || sizesEl.dataset.loaded === handle) return;

    try {
      const res     = await fetch(`/products/${handle}.js`);
      const product = await res.json();
      sizesEl.dataset.loaded = handle;

      sizesEl.innerHTML = product.variants.map(v => `
        <button class="mm-size-pill${!v.available ? ' is-soldout' : ''}"
                data-variant-id="${v.id}"
                ${!v.available ? 'disabled' : ''}>
          ${v.title === 'Default Title' ? 'Único' : v.title}
        </button>
      `).join('');

      sizesEl.querySelectorAll('.mm-size-pill:not(.is-soldout)').forEach(pill => {
        pill.addEventListener('click', async () => {
          sizesEl.querySelectorAll('.mm-size-pill').forEach(p => p.classList.remove('is-selected'));
          pill.classList.add('is-selected');
          const originalText = pill.textContent.trim();
          pill.textContent = '✓';

          await this.addToCart(pill.dataset.variantId, product.vendor, product.title);

          setTimeout(() => {
            pill.textContent = originalText;
            this.closeTray(tray);
          }, 700);
        });
      });

    } catch (err) {
      console.error('Size tray:', err);
      if (sizesEl) sizesEl.innerHTML =
        '<p style="font-size:11px;color:var(--mm-error);padding:4px 0;">Error. Intenta de nuevo.</p>';
    }
  },

  closeTray(tray) {
    tray.classList.remove('is-open');
    setTimeout(() => { tray.hidden = true; }, 280);
  },

  async addToCart(variantId, vendor, title) {
    try {
      await fetch('/cart/add.js', {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({ id: variantId, quantity: 1 }),
      });

      // Refresh cart count badge
      const cartRes  = await fetch('/cart.js');
      const cartData = await cartRes.json();
      document.querySelectorAll('[data-mm-cart-count]').forEach(el => {
        el.textContent = cartData.item_count;
        el.style.display = cartData.item_count > 0 ? 'flex' : 'none';
      });

      const label = vendor ? `${vendor} — Agregado` : 'Agregado al carrito';
      MONARA.showToast(label);

    } catch (err) {
      console.error('Inline add to cart:', err);
    }
  },
};

/* ── Home PLP — AJAX product loader ─────────────────────── */
MONARA.HomePLP = {
  activeCollection: null,
  activeTag:        null,
  currentPage:      1,
  fetchPage:        1,
  exhausted:        false,
  loading:          false,
  totalCount:       0,

  init() {
    if (!document.getElementById('mm-home-plp')) return;

    // Category button clicks
    document.querySelectorAll('.mm-hero__cat-item').forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        const handle = btn.dataset.collection;
        if (!handle) return;

        if (this.activeCollection === handle) {
          this.close(); return;
        }

        document.querySelectorAll('.mm-hero__cat-item').forEach(b =>
          b.classList.remove('is-active')
        );
        btn.classList.add('is-active');

        this.activeCollection = handle;
        this.activeTag        = null;
        this.open(handle);
      });
    });

    document.getElementById('mm-plp-close')?.addEventListener('click', () => this.close());

    document.getElementById('mm-home-sort')?.addEventListener('change', e => {
      this.currentPage = 1;
      this.load(true);
    });

    document.getElementById('mm-loadmore-btn')?.addEventListener('click', () => {
      this.load(false); // fetchPage avanza internamente en load()
    });

    this.initStickyHeader();
  },

  initStickyHeader() {
    const hero   = document.querySelector('.mm-hero');
    const header = document.querySelector('.mm-home-plp__header');
    if (!hero || !header) return;

    // Sombra cuando el hero sale de vista
    const obs = new IntersectionObserver(([entry]) => {
      header.classList.toggle('is-sticky', !entry.isIntersecting);
    }, { threshold: 0 });
    obs.observe(hero);

    let lastY   = window.scrollY;
    let ticking = false;

    window.addEventListener('scroll', () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        const currentY  = window.scrollY;
        const goingDown = currentY > lastY;
        const heroH     = hero.offsetHeight || 300;
        const pastHero  = currentY > heroH;
        // Solo mostrar la barra cuando el usuario esté cerca del inicio del PLP.
        // Fuera de esa zona (en medio de los productos), la barra permanece oculta
        // para evitar el efecto flotante.
        const nearPLPTop = currentY <= heroH + 120;

        if (goingDown && pastHero) {
          header.classList.add('is-hidden');
        } else if (!goingDown && nearPLPTop) {
          header.classList.remove('is-hidden');
        }
        // Si va hacia arriba pero aún está en los productos → barra sigue oculta

        lastY   = currentY;
        ticking = false;
      });
    }, { passive: true });
  },

  open(handle) {
    const plp = document.getElementById('mm-home-plp');
    if (!plp) return;

    plp.hidden = false;
    plp.classList.add('is-visible');

    const catData = window.mmHomeCategories?.[handle];
    const label   = document.getElementById('mm-active-cat-label');
    if (label) label.textContent = catData?.label || handle;

    this.renderChips(handle);
    this.currentPage = 1;
    this.load(true);

    setTimeout(() =>
      plp.scrollIntoView({ behavior: 'smooth', block: 'start' }), 60
    );
  },

  close() {
    const plp = document.getElementById('mm-home-plp');
    plp?.classList.remove('is-visible');
    setTimeout(() => { if (plp) plp.hidden = true; }, 300);

    document.querySelectorAll('.mm-hero__cat-item').forEach(b =>
      b.classList.remove('is-active')
    );

    this.activeCollection = null;
    this.activeTag        = null;
  },

  renderChips(handle) {
    const container = document.getElementById('mm-home-chips');
    if (!container) return;

    const catData = window.mmHomeCategories?.[handle];
    if (!catData?.chips?.length) { container.innerHTML = ''; return; }

    // No pre-filter by tag — show all products first; chips filter on click
    this.activeTag = null;

    container.innerHTML = catData.chips.map((chip) => `
      <button class="mm-filter-chip"
              data-tag="${chip.tag || ''}" data-mm-home-chip>
        ${chip.label}
      </button>
    `).join('');

    container.querySelectorAll('[data-mm-home-chip]').forEach(chip => {
      chip.addEventListener('click', () => {
        container.querySelectorAll('[data-mm-home-chip]').forEach(c =>
          c.classList.remove('is-active')
        );
        chip.classList.add('is-active');
        this.activeTag   = chip.dataset.tag || null;
        this.currentPage = 1;
        this.load(true);
      });
    });
  },

  async load(reset) {
    if (this.loading) return;
    this.loading = true;

    const grid    = document.getElementById('mm-home-grid');
    const toolbar = document.getElementById('mm-home-toolbar');
    const countEl = document.getElementById('mm-home-count');
    const loadBtn = document.getElementById('mm-home-loadmore');
    const sort    = document.getElementById('mm-home-sort')?.value || 'created-descending';

    if (!grid) { this.loading = false; return; }

    if (reset) {
      grid.innerHTML  = this.skeletonHtml();
      this.totalCount = 0;
      this.fetchPage  = 1;
      this.exhausted  = false;
    }

    const PAGE_SIZE = 12;

    try {
      let collected = [];

      while (collected.length < PAGE_SIZE && !this.exhausted) {
        const url = `/collections/${this.activeCollection}/products.json` +
                    `?limit=250&page=${this.fetchPage}&sort_by=${sort}`;

        console.log('[MM HomePLP] Fetching:', url);

        const res = await fetch(url);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data  = await res.json();
        const batch = data.products || [];

        console.log('[MM HomePLP] Batch recibido:', batch.length, 'productos');
        if (batch.length > 0) {
          console.log('[MM HomePLP] Primer producto:', batch[0].title, '| tags:', batch[0].tags);
          console.log('[MM HomePLP] activeTag:', this.activeTag);
        }

        if (!batch.length) { this.exhausted = true; break; }

        const filtered = this.activeTag
          ? batch.filter(p => {
              const tags = Array.isArray(p.tags)
                ? p.tags
                : (typeof p.tags === 'string' ? p.tags.split(',').map(t => t.trim()) : []);
              return tags.some(t => t.toLowerCase() === this.activeTag.toLowerCase());
            })
          : batch;

        console.log('[MM HomePLP] Después de filtro:', filtered.length, 'productos');

        collected = collected.concat(filtered);
        this.fetchPage++;

        if (batch.length < 250) this.exhausted = true;
      }

      if (reset) grid.innerHTML = '';

      // toolbar oculto intencionalmente

      if (!collected.length && reset) {
        grid.innerHTML = '<p class="mm-home-plp__empty">No hay productos en esta categoría.</p>';
        if (loadBtn) loadBtn.style.display = 'none';
        return;
      }

      this.totalCount += collected.length;
      if (countEl) countEl.textContent = `${this.totalCount} pieza${this.totalCount !== 1 ? 's' : ''}`;

      collected.forEach((product, i) => {
        const globalIndex = reset ? i : grid.children.length + i;
        const card = this.buildCard(product, globalIndex);
        card.style.opacity   = '0';
        card.style.transform = 'translateY(12px)';
        grid.appendChild(card);
        requestAnimationFrame(() => {
          card.style.transition = 'opacity 0.35s ease, transform 0.35s ease';
          card.style.opacity    = '1';
          card.style.transform  = '';
        });
      });

      MONARA.HomeVideoGrid.bindVideos(grid);
      MONARA.Wishlist.init(); // re-init to catch new wishlist buttons

      if (loadBtn) {
        loadBtn.style.display = this.exhausted ? 'none' : 'flex';
      }

    } catch (err) {
      console.error('HomePLP load:', err);
      if (reset) grid.innerHTML =
        '<p class="mm-home-plp__empty">Error al cargar. Intenta de nuevo.</p>';
    } finally {
      this.loading = false;
    }
  },

  buildCard(product, index) {
    const rawPrice     = product.price_min ?? product.price ?? product.variants?.[0]?.price ?? 0;
    const rawCompare   = product.compare_at_price_min ?? product.compare_at_price ?? 0;
    const price        = MONARA.formatMoneyFromPesos(rawPrice);
    const comparePrice = rawCompare > rawPrice ? MONARA.formatMoneyFromPesos(rawCompare) : null;
    const onSale = !!comparePrice;
    const isNew  = product.tags?.some(t => ['nuevo','new','nueva-llegada'].includes(t));
    const isWide = (index % 5 === 4); // every 5th card spans 2 cols

    const imgBase = (src) => {
      const s = typeof src === 'string' ? src : src?.src || '';
      return s ? s.replace(/\?.*/, '') + '?width=600' : '';
    };
    const mainImg = imgBase(product.featured_image) || imgBase(product.images?.[0]);
    const allImgs = product.images?.map(img => imgBase(img.src || img)) || [mainImg];

    // Swatches
    const seenColors = new Set();
    const swatchHtml = (product.variants || []).slice(0, 6).reduce((acc, v) => {
      if (!seenColors.has(v.option1) && v.featured_image?.src) {
        seenColors.add(v.option1);
        acc += `<span class="mm-card__swatch"
          data-mm-swatch-img="${imgBase(v.featured_image.src)}"
          style="background:${v.option1.toLowerCase().replace(/\s+/g,'-')};"
          title="${v.option1}"></span>`;
      }
      return acc;
    }, '');

    const dotsHtml = allImgs.length > 1
      ? `<div class="mm-card__dots">${allImgs.slice(0,4).map((_, i) =>
          `<span class="mm-card__dot${i === 0 ? ' is-active' : ''}"></span>`
        ).join('')}</div>`
      : '';

    const arrowsHtml = allImgs.length > 1
      ? `<div class="mm-card__nav-arrows">
           <button class="mm-card__arrow mm-card__arrow--prev" aria-label="Foto anterior">‹</button>
           <button class="mm-card__arrow mm-card__arrow--next" aria-label="Siguiente">›</button>
         </div>`
      : '';

    const article = document.createElement('article');
    article.className = `mm-card mm-home-card${isWide ? ' mm-masonry__card-wide' : ''}`;
    article.dataset.productId     = product.id;
    article.dataset.productHandle = product.handle;

    article.innerHTML = `
      <div class="mm-card__media">
        <a href="/products/${product.handle}" aria-label="${product.title}">
          <img class="mm-card__img" src="${mainImg}" alt="${product.title}" loading="lazy">
        </a>
        ${isNew    ? '<span class="mm-card__badge mm-card__badge--new">NUEVA LLEGADA</span>' : ''}
        ${!isNew && onSale ? '<span class="mm-card__badge mm-card__badge--sale">SALE</span>' : ''}
        <button class="mm-card__wishlist" data-mm-wishlist="${product.id}" aria-label="Wishlist">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
            <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>
          </svg>
        </button>
        ${arrowsHtml}
        <div class="mm-card__quick-add">
          <button class="mm-card__add-btn"
                  data-mm-inline-size="${product.handle}"
                  aria-label="Ver tallas">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
            </svg>
          </button>
        </div>
        ${dotsHtml}
      </div>
      <div class="mm-card__size-tray" hidden>
        <div class="mm-card__size-tray__inner">
          <div class="mm-card__size-tray__header">
            <span>Selecciona talla</span>
          </div>
          <div class="mm-card__size-tray__sizes" data-product-handle="${product.handle}">
            <div class="mm-skeleton" style="height:32px;width:100%;"></div>
          </div>
        </div>
      </div>
      <div class="mm-card__info">
        <p class="mm-card__brand">${(product.vendor || 'MARCA').toUpperCase()}</p>
        <p class="mm-card__title">
          <a href="/products/${product.handle}">${product.title}</a>
        </p>
        ${swatchHtml ? `<div class="mm-card__swatches">${swatchHtml}</div>` : ''}
        <div class="mm-card__price">
          <span class="mm-card__price-main${onSale ? ' mm-card__price-sale' : ''}">${price}</span>
          ${comparePrice ? `<span class="mm-card__price-compare">${comparePrice}</span>` : ''}
        </div>
      </div>
    `;

    // Wire photo navigation
    if (allImgs.length > 1) {
      let current = 0;
      const img   = article.querySelector('.mm-card__img');
      const dots  = article.querySelectorAll('.mm-card__dot');

      const goTo = (idx) => {
        current = (idx + allImgs.length) % allImgs.length;
        if (img) img.src = allImgs[current];
        dots.forEach((d, i) => d.classList.toggle('is-active', i === current));
      };

      article.querySelector('.mm-card__arrow--prev')?.addEventListener('click', e => {
        e.preventDefault(); e.stopPropagation(); goTo(current - 1);
      });
      article.querySelector('.mm-card__arrow--next')?.addEventListener('click', e => {
        e.preventDefault(); e.stopPropagation(); goTo(current + 1);
      });

      // Swipe táctil (móvil) sobre la imagen del card
      const media = article.querySelector('.mm-card__media');
      if (media) {
        let touchStartX = 0, touchStartY = 0, isSwiping = false;
        const SWIPE_THRESHOLD = 30; // px mínimos para contar como swipe

        media.addEventListener('touchstart', e => {
          touchStartX = e.touches[0].clientX;
          touchStartY = e.touches[0].clientY;
          isSwiping = false;
        }, { passive: true });

        media.addEventListener('touchmove', e => {
          const dx = e.touches[0].clientX - touchStartX;
          const dy = e.touches[0].clientY - touchStartY;
          if (Math.abs(dx) > Math.abs(dy) && Math.abs(dx) > 10) {
            isSwiping = true; // gesto horizontal: bloquea el link/scroll vertical
            e.preventDefault();
          }
        }, { passive: false });

        media.addEventListener('touchend', e => {
          if (!isSwiping) return;
          const dx = e.changedTouches[0].clientX - touchStartX;
          if (Math.abs(dx) >= SWIPE_THRESHOLD) {
            goTo(current + (dx < 0 ? 1 : -1));
          }
          isSwiping = false;
        }, { passive: true });

        // Evita que el swipe horizontal dispare la navegación al PDP
        media.querySelector('a')?.addEventListener('click', e => {
          if (isSwiping) { e.preventDefault(); }
        });
      }
    }

    return article;
  },

  skeletonHtml() {
    return Array.from({ length: 6 }, (_, i) => `
      <article class="mm-card${i % 5 === 4 ? ' mm-masonry__card-wide' : ''}">
        <div class="mm-card__media">
          <div class="mm-skeleton" style="width:100%;aspect-ratio:3/4;"></div>
        </div>
        <div class="mm-card__info" style="padding-top:8px;">
          <div class="mm-skeleton" style="height:10px;width:50%;margin-bottom:8px;"></div>
          <div class="mm-skeleton" style="height:13px;width:75%;margin-bottom:8px;"></div>
          <div class="mm-skeleton" style="height:15px;width:35%;"></div>
        </div>
      </article>
    `).join('');
  },
};

/* ── Init ────────────────────────────────────────────────── */
document.addEventListener('DOMContentLoaded', () => {
  MONARA.CartDrawer.init();
  MONARA.QuickAdd.init();
  MONARA.CompleteLook.init();
  MONARA.SizeGuide.init();
  MONARA.SwatchSwap.init();
  MONARA.PDPGallery.init();
  MONARA.PDPVariants.init();
  MONARA.InfiniteScroll.init();
  MONARA.ClipsFeed.init();
  MONARA.Carousels.init();
  MONARA.MobileMenu.init();
  MONARA.Wishlist.init();
  MONARA.FilterChips.init();
  MONARA.Newsletter.init();
  MONARA.initToast();
  MONARA.HomeVideoGrid.init();
  MONARA.InlineSizePicker.init();
  MONARA.HomePLP.init();
});
